import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Table,
  Tag,
  Statistic,
  message,
  Row,
  Col,
  DatePicker,
  Select,
  Progress,
  Button,
  Tooltip,
  Avatar,
} from "antd";
import { Bar, Line } from "react-chartjs-2";
import {
  UserOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import moment from "moment";

const { RangePicker } = DatePicker;
const { Option } = Select;

// Process and enrich loan application data
const processLoanApplications = (loans) => {
  // Log first loan to understand the structure
  if (loans && loans.length > 0) {
    console.log("First loan data structure:", loans[0]);
  }

  return loans.map((loan) => {
    // Calculate processing time in days
    const createdDate = moment(loan.date_created);
    const statusDate = loan.date_updated ? moment(loan.date_updated) : moment();
    const processingTime = Math.max(1, statusDate.diff(createdDate, "days"));

    // Create a readable name for the assigned officer
    const officerFirstName = loan.assigned_to?.first_name || "";
    const officerLastName = loan.assigned_to?.last_name || "";
    const assigned_officer =
      officerFirstName && officerLastName
        ? `${officerFirstName} ${officerLastName}`
        : "Unassigned";

    // Determine the current stage - ensure we handle different status formats
    const status = loan.status || "";
    const current_stage = {
      label: status,
      value: status.toLowerCase(),
    };

    // Ensure amount is a number
    let amount = 0;
    if (loan.amount) {
      amount =
        typeof loan.amount === "string" ? parseFloat(loan.amount) : loan.amount;
    }

    return {
      ...loan,
      processing_time: processingTime,
      assigned_officer,
      current_stage,
      amount: amount,
    };
  });
};

const officerPerformanceMetrics = (applications) => {
  // Log the status values to understand what we're working with
  console.log(
    "Status values in applications:",
    applications.map((app) => app.current_stage.label)
  );

  const officers = {};

  applications.forEach((app) => {
    const officerKey = app.assigned_officer;
    if (!officerKey) return; // Skip if no officer assigned

    if (!officers[officerKey]) {
      officers[officerKey] = {
        officerName: officerKey,
        totalApplications: 0,
        approved: 0,
        disbursed: 0,
        totalAmount: 0,
        processingTimes: [],
        statusCounts: {
          overdue: 0,
          defaulted: 0,
          rejected: 0,
        },
      };
    }

    const officer = officers[officerKey];
    officer.totalApplications++;
    officer.totalAmount += app.amount || 0;

    // Track statuses - handle case insensitively
    const status = (app.current_stage.label || "").toLowerCase();

    // More flexible status checking
    if (status.includes("approve")) officer.approved++;
    if (status.includes("disburse")) officer.disbursed++;
    if (status.includes("overdue")) officer.statusCounts.overdue++;
    if (status.includes("default")) officer.statusCounts.defaulted++;
    if (status.includes("reject")) officer.statusCounts.rejected++;

    // Track processing time if it exists
    if (app.processing_time) {
      officer.processingTimes.push(app.processing_time);
    }
  });

  // Calculate averages and percentages with safety checks for division by zero
  return Object.values(officers).map((officer) => {
    // Safety checks to prevent division by zero
    const totalApps = officer.totalApplications || 1; // Avoid division by zero
    const totalDisbursed = officer.disbursed || 1; // Avoid division by zero
    const processingTimesCount = officer.processingTimes.length || 1; // Avoid division by zero

    return {
      ...officer,
      approvalRate: ((officer.approved / totalApps) * 100).toFixed(1),
      disbursementRate: ((officer.disbursed / totalApps) * 100).toFixed(1),
      avgProcessingTime: (
        officer.processingTimes.reduce((a, b) => a + b, 0) /
        processingTimesCount
      ).toFixed(1),
      riskRatio: (
        ((officer.statusCounts.overdue + officer.statusCounts.defaulted) /
          totalDisbursed) *
        100
      ).toFixed(1),
    };
  });
};

const LoanOfficerPerformanceReport = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [loanProducts, setLoanProducts] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: [moment().subtract(30, "days"), moment()],
    product: "all",
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!company?.id || !token) return;

      try {
        setLoading(true);
        const startDate = filters.dateRange[0].format("YYYY-MM-DD");
        const endDate = filters.dateRange[1].format("YYYY-MM-DD");

        // Log the API URL being called
        const apiUrl = `https://zedlenders.pickmesms.com/items/loans?filter[company]=${company.id}&filter[date_created][_between]=${startDate},${endDate}&fields=*,borrower.first_name,borrower.last_name,loan_product.loan_name,assigned_to.first_name,assigned_to.last_name,date_updated,status,amount`;
        console.log("API URL:", apiUrl);

        const response = await axios.get(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Log raw API response to understand the data structure
        console.log("API response:", response.data);

        const productsResponse = await axios.get(
          `https://zedlenders.pickmesms.com/items/loan_products?filter[company]=${company.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        let filteredData = response.data.data || [];

        // Check if we have data
        if (filteredData.length === 0) {
          console.log("No loan data returned from API");
          message.warning("No loan data found for the selected date range");
        }

        if (filters.product !== "all") {
          filteredData = filteredData.filter(
            (loan) => loan.loan_product?.id == filters.product
          );
        }

        const processed = processLoanApplications(filteredData);
        console.log("Processed applications:", processed);

        setApplications(processed);
        setLoanProducts(productsResponse.data.data || []);
      } catch (error) {
        console.error("Failed to fetch data:", error);
        message.error(
          "Failed to load performance data: " +
            (error.message || "Unknown error")
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [company?.id, token, filters]);

  const metricsData = officerPerformanceMetrics(applications);

  // Debug the final metrics data
  console.log("Final metrics data:", metricsData);

  // Chart configurations
  const productivityChart = {
    labels: metricsData.map((o) => o.officerName),
    datasets: [
      {
        label: "Applications Processed",
        data: metricsData.map((o) => o.totalApplications),
        backgroundColor: "#1890ff",
      },
      {
        label: "Amount Disbursed (ZMW)",
        data: metricsData.map((o) => o.totalAmount / 1000), // Scale down for chart
        backgroundColor: "#52c41a",
        yAxisID: "y-axis-amount",
      },
    ],
  };

  const efficiencyChart = {
    labels: metricsData.map((o) => o.officerName),
    datasets: [
      {
        label: "Avg Processing Days",
        data: metricsData.map((o) => o.avgProcessingTime),
        borderColor: "#faad14",
        fill: false,
      },
      {
        label: "Approval Rate %",
        data: metricsData.map((o) => o.approvalRate),
        borderColor: "#13c2c2",
        fill: false,
      },
    ],
  };

  const columns = [
    {
      title: "Loan Officer",
      dataIndex: "officerName",
      render: (text) => (
        <div className="officer-header">
          <Avatar icon={<UserOutlined />} style={{ marginRight: 8 }} />
          {text === "Unassigned" ? (
            <span style={{ color: "#999" }}>Unassigned</span>
          ) : (
            text
          )}
        </div>
      ),
      sorter: (a, b) => a.officerName.localeCompare(b.officerName),
    },
    {
      title: "Volume",
      children: [
        {
          title: "Total Apps",
          dataIndex: "totalApplications",
          sorter: (a, b) => a.totalApplications - b.totalApplications,
          render: (val) => <Tag color="blue">{val}</Tag>,
        },
        {
          title: "Disbursed",
          dataIndex: "disbursed",
          sorter: (a, b) => a.disbursed - b.disbursed,
          render: (val, record) => `${val} (${record.disbursementRate}%)`,
        },
      ],
    },
    {
      title: "Efficiency",
      children: [
        {
          title: "Processing Days",
          dataIndex: "avgProcessingTime",
          sorter: (a, b) => a.avgProcessingTime - b.avgProcessingTime,
          render: (val) => (
            <>
              <ClockCircleOutlined /> {val}d
            </>
          ),
        },
        {
          title: "Approval Rate",
          dataIndex: "approvalRate",
          sorter: (a, b) => a.approvalRate - b.approvalRate,
          render: (val) => <Progress percent={val} size="small" />,
        },
      ],
    },
    {
      title: "Portfolio Health",
      children: [
        {
          title: "Risk Ratio",
          dataIndex: "riskRatio",
          render: (val) => (
            <Tag color={val > 15 ? "error" : val > 5 ? "warning" : "success"}>
              {val}%
            </Tag>
          ),
        },
        {
          title: "Active Loans",
          dataIndex: ["statusCounts", "overdue"],
          render: (val, record) => (
            <Tooltip
              title={`Overdue: ${val} | Defaulted: ${record.statusCounts.defaulted}`}
            >
              {record.disbursed - val - record.statusCounts.defaulted}
            </Tooltip>
          ),
        },
      ],
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      render: (val) => `ZMW ${val.toLocaleString()}`,
      sorter: (a, b) => a.totalAmount - b.totalAmount,
    },
  ];

  const exportData = () => {
    // CSV export implementation similar to previous example
    // Would export all metricsData in CSV format
  };

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false} className="performance-report">
        <Row justify="space-between" align="middle" className="report-header">
          <Col>
            <h1>Loan Officer Performance Dashboard</h1>
            <p>
              Track individual performance metrics and portfolio health
              indicators
            </p>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={exportData}
              disabled={loading}
            >
              Export Data
            </Button>
          </Col>
        </Row>

        <Row gutter={16} className="report-controls">
          <Col span={12}>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              style={{ width: "100%" }}
            />
          </Col>
          <Col span={12}>
            <Select
              style={{ width: "100%" }}
              placeholder="Filter by Loan Product"
              onChange={(value) => setFilters({ ...filters, product: value })}
              loading={loading}
            >
              <Option value="all">All Products</Option>
              {loanProducts.map((product) => (
                <Option key={product.id} value={product.id}>
                  {product.loan_name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row gutter={24} className="performance-metrics">
          <Col span={12}>
            <Card title="Productivity Overview">
              <Bar
                data={productivityChart}
                options={{
                  responsive: true,
                  scales: {
                    y: { beginAtZero: true },
                    "y-axis-amount": {
                      position: "right",
                      grid: { drawOnChartArea: false },
                    },
                  },
                }}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Efficiency Trends">
              <Line
                data={efficiencyChart}
                options={{
                  responsive: true,
                  scales: {
                    y: { beginAtZero: true },
                  },
                }}
              />
            </Card>
          </Col>
        </Row>

        <Card
          title="Detailed Performance Analysis"
          bordered={false}
          className="performance-table"
        >
          <Table
            columns={columns}
            dataSource={metricsData}
            rowKey="officerName"
            loading={loading}
            scroll={{ x: true }}
            pagination={{ pageSize: 5 }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>Totals</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    {metricsData.reduce(
                      (sum, o) => sum + o.totalApplications,
                      0
                    )}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    {metricsData.reduce((sum, o) => sum + o.disbursed, 0)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    {metricsData
                      .reduce((sum, o) => sum + o.totalAmount, 0)
                      .toLocaleString()}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>
      </Card>
    </div>
  );
};

export default LoanOfficerPerformanceReport;
